/**
 * Komponent WiseChart - komponent do wyświetlania wykresów z parametrami range/date_range
 */

import React, { FC, useMemo } from 'react';
import { useAppTheme } from 'theme/useAppTheme';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { LinePlot, LineHighlightPlot } from '@mui/x-charts/LineChart';
import { MarkPlot } from '@mui/x-charts/LineChart';
import { BarPlot } from '@mui/x-charts/BarChart';
import { ResponsiveChartContainer } from '@mui/x-charts/ResponsiveChartContainer';
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis';
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis';
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip';

import WiseChartTopBar from 'components/wise/WiseChart/components/WiseChartTopBar';
import { useWiseChartData } from 'components/wise/WiseChart/hooks/useWiseChartData';
import { useWiseChartParameters } from 'components/wise/WiseChart/hooks/useWiseChartParameters';
import { IWiseChartProps } from 'components/wise/types';

import styles from 'theme/components/wise/WiseChart/WiseChart.module.scss';

const TREND_LINE_SERIES_ID = 'trend-line';

function getLinearTrendData(values: (number | null)[]): number[] {
  const points: { x: number; y: number }[] = values
    .map((y, x) => (y != null ? { x, y } : null))
    .filter((p): p is { x: number; y: number } => p !== null);
  const n = points.length;
  if (n < 2) return values.map((v) => (v != null ? v : 0));

  const sumX = points.reduce((s, p) => s + p.x, 0);
  const sumY = points.reduce((s, p) => s + p.y, 0);
  const sumXY = points.reduce((s, p) => s + p.x * p.y, 0);
  const sumX2 = points.reduce((s, p) => s + p.x * p.x, 0);
  const m = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX) || 0;
  const b = (sumY - m * sumX) / n;

  return values.map((_, i) => m * i + b);
}

const WiseChart: FC<IWiseChartProps> = ({
  label,
  parameters = [],
  loadDataParams,
  requestParams,
  chartType = 'line',
  showLegend = false,
  showTrendLine = false
}) => {
  const theme = useAppTheme();
  const { i18n } = useTranslation();
  const chartSecondaryColor = theme.palette.app.secondaryLight;

  // Hook do zarządzania danymi
  const { chartData, queryParams, setQueryParams, isLoading } = useWiseChartData({
    loadDataParams,
    requestParams,
    parameters
  });

  // Hook do zarządzania logiką parametrów
  const { activeParameters, handleClearParameters, updateParameter } = useWiseChartParameters({
    parameters,
    queryParams,
    setQueryParams
  });

  const xAxisData = useMemo(() => {
    if (!chartData?.chart_data?.labels) {
      return [];
    }
    return chartData.chart_data.labels.map((label: string) => new Date(label));
  }, [chartData]);

  // Dla wykresu słupkowego: oś X musi być typu "band" – używamy etykiet jako kategorii
  // Dla kwartału (3), 6 mies. (6) i roku (12) pokazujemy nazwę miesiąca
  const xAxisBandData = useMemo(() => {
    if (chartType !== 'bar' || !chartData?.chart_data?.labels) return [];
    const labels = chartData.chart_data.labels;
    const locale = i18n.language === 'pl' ? 'pl-PL' : i18n.language === 'en' ? 'en-GB' : i18n.language;
    const showMonthName = [3, 6, 12].includes(labels.length);
    return labels.map((label: string) => {
      const d = new Date(label);
      if (showMonthName) {
        return d.toLocaleDateString(locale, { month: 'long' });
      }
      return d.toLocaleDateString(locale, { day: '2-digit', month: '2-digit' });
    });
  }, [chartData, chartType, i18n.language]);

  // Przygotowanie danych do wykresu
  const chartSeries = useMemo(() => {
    if (!chartData?.chart_data?.series || !chartData?.chart_data?.labels) {
      return [];
    }

    const series = chartData.chart_data.series.map((serie: any) =>
      chartType === 'bar'
        ? {
            type: 'bar' as const,
            data: serie.data,
            label: serie.name,
            color: chartSecondaryColor
          }
        : {
            type: 'line' as const,
            data: serie.data,
            label: serie.name,
            color: chartSecondaryColor,
            curve: 'linear' as const,
            showMark: true
          }
    );

    if (chartType === 'line' && showTrendLine && series.length > 0) {
      const firstSeriesData = (chartData.chart_data.series[0] as { data: (number | null)[] }).data;
      const trendData = getLinearTrendData(firstSeriesData);
      series.push({
        type: 'line' as const,
        id: TREND_LINE_SERIES_ID,
        data: trendData,
        label: 'Linia trendu',
        color: theme.palette.app.colorOrange,
        curve: 'linear' as const,
        showMark: false
      });
    }

    return series;
  }, [chartData, chartType, chartSecondaryColor, showTrendLine]);

  // Aktualizacja amount z danych jeśli dostępne
  const displayAmount = useMemo(() => {
    if (chartData?.amount !== undefined) {
      return String(chartData.amount);
    }
    return '';
  }, [chartData]);

  return (
    <div className={classnames(styles.componentWrapper, 'WiseChart', 'StylePath-WiseChart')}>
      <div className={styles.chartContainer}>
        <WiseChartTopBar
          label={label}
          amount={displayAmount}
          parameters={parameters}
          queryParams={queryParams}
          activeParameters={activeParameters}
          handleClearParameters={handleClearParameters}
          updateParameter={updateParameter}
        />

        {isLoading ? (
          <div className={styles.loading}>Ładowanie danych...</div>
        ) : chartData && chartSeries.length > 0 && xAxisData.length > 0 ? (
          <div
            className={classnames(styles.chartContent, {
              [styles.chartContentWithTrend]: chartType === 'line' && showTrendLine
            })}>
            {showLegend && (
              <div className={styles.legendWrapper}>
                {chartSeries.map((serie: { label: string; color?: string }, index: number) => (
                  <div key={index} className={styles.legendItem}>
                    <span
                      className={styles.legendMark}
                      style={{ backgroundColor: serie.color ?? chartSecondaryColor }}
                    />
                    <span>{serie.label}</span>
                  </div>
                ))}
              </div>
            )}
            <ResponsiveChartContainer
              series={chartSeries}
              height={400}
              margin={{ top: 20, right: 20, bottom: 40, left: 80 }}
              xAxis={[
                chartType === 'bar'
                  ? {
                      id: 'x-axis',
                      data: xAxisBandData,
                      scaleType: 'band'
                    }
                  : {
                      id: 'x-axis',
                      data: xAxisData,
                      scaleType: 'time',
                      valueFormatter: (value: Date) => {
                        const locale = i18n.language === 'pl' ? 'pl-PL' : i18n.language === 'en' ? 'en-GB' : i18n.language;
                        return value.toLocaleDateString(locale, {
                          day: '2-digit',
                          month: '2-digit'
                        });
                      }
                    }
              ]}
              yAxis={[
                {
                  id: 'y-axis',
                  label: chartSeries[0]?.label
                }
              ]}>
              {chartType === 'bar' ? (
                <BarPlot borderRadius={6} />
              ) : (
                <>
                  <LinePlot />
                  <LineHighlightPlot />
                  <MarkPlot />
                </>
              )}
              <ChartsXAxis
                position="bottom"
                axisId="x-axis"
                tickLabelStyle={{
                  fontSize: 12
                }}
              />
              <ChartsYAxis
                position="left"
                axisId="y-axis"
                slotProps={{
                  axisLabel: {
                    x: -70
                  }
                }}
                tickLabelStyle={{
                  fontSize: 12
                }}
                valueFormatter={(value: number) => {
                  return new Intl.NumberFormat('pl-PL', {
                    style: 'currency',
                    currency: 'PLN',
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 0
                  }).format(value);
                }}
              />
              <ChartsTooltip />
            </ResponsiveChartContainer>
          </div>
        ) : (
          <div className={styles.noData}>Brak danych do wyświetlenia</div>
        )}
      </div>
    </div>
  );
};

export default WiseChart;
